import { Card, Descriptions, Drawer, Space, Tag, Typography } from 'antd';
import { Link } from 'react-router-dom';
import { adminLinks } from '../../lib/adminLinks';
import {
  CATEGORY_LABEL,
  SEVERITY_COLOR,
  SOURCE_LABEL,
  STATUS_COLOR,
  eventLabel,
  formatLogTime,
  refName,
  type LogRefs,
  type LogRow,
} from './logsApi';

/**
 * One log row, beside the list (right-side drawer — Back closes it, the list
 * keeps its filters and page). Formatted JSON for metadata / old / new values.
 * Technical fields (IP, user agent, device, error text, stack) arrive only for
 * admins with logs.view_sensitive — the API blanks them otherwise, and this
 * drawer simply hides empty sections.
 */
export function LogDetailDrawer({
  row,
  refs,
  open,
  onClose,
  onFilter,
}: {
  row: LogRow | null;
  refs?: LogRefs;
  open: boolean;
  onClose: () => void;
  /** Narrow the list to a request / correlation id (Logs page only). */
  onFilter?: (patch: { request_id?: string; correlation_id?: string }) => void;
}) {
  return (
    <Drawer
      width={Math.min(680, typeof window !== 'undefined' ? window.innerWidth : 680)}
      title={row ? eventLabel(row) : ''}
      open={open}
      onClose={onClose}
      destroyOnClose
    >
      {row && <LogDetailBody row={row} refs={refs} onFilter={onFilter} />}
    </Drawer>
  );
}

function Json({ value }: { value: unknown }) {
  return (
    <pre
      style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 360, overflow: 'auto' }}
    >
      {typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
    </pre>
  );
}

function IdText({ id, to }: { id: string | null; to?: string | null }) {
  if (!id) return <span>—</span>;
  return to ? (
    <Space size={6}>
      <Link to={to}>{id}</Link>
      <Typography.Text copyable={{ text: id }} />
    </Space>
  ) : (
    <Typography.Text copyable style={{ fontSize: 12 }}>
      {id}
    </Typography.Text>
  );
}

function actorLine(row: LogRow, refs?: LogRefs): string {
  if (row.actorType === 'system') return 'System';
  const kind = row.actorType === 'admin' ? 'admin' : 'user';
  const name = refName(refs, kind, row.actorId);
  const who = row.actorType === 'admin' ? 'Admin' : row.actorType === 'listener' ? 'Listener' : 'User';
  return name ? `${who} · ${name}` : row.actorId ? `${who} · ${row.actorId.slice(0, 8)}` : who;
}

function targetLink(type: string, id: string | null): string | null {
  if (!id) return null;
  switch (type) {
    case 'User':
      return adminLinks.user(id);
    case 'Listener':
      return adminLinks.listener(id);
    case 'CallSession':
      return adminLinks.call(id);
    case 'PaymentOrder':
      return adminLinks.recharge(id);
    default:
      return null;
  }
}

export function LogDetailBody({
  row,
  refs,
  onFilter,
}: {
  row: LogRow;
  refs?: LogRefs;
  onFilter?: (patch: { request_id?: string; correlation_id?: string }) => void;
}) {
  const meta = row.metadata ?? {};
  const { stack, ...metaRest } = meta as { stack?: string } & Record<string, unknown>;
  const userName = refName(refs, 'user', row.userId);
  const listenerName = refName(refs, 'listener', row.listenerId);
  const endpoint = typeof meta.endpoint === 'string' ? meta.endpoint : typeof meta.path === 'string' ? meta.path : null;
  const duration = typeof meta.durationMs === 'number' ? `${meta.durationMs} ms` : null;

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Space wrap size={6}>
        <Tag>{CATEGORY_LABEL[row.eventCategory] ?? row.eventCategory}</Tag>
        <Tag color={SEVERITY_COLOR[row.severity]}>{row.severity}</Tag>
        <Tag color={STATUS_COLOR[row.status]}>{row.status}</Tag>
        {row.kind === 'system' && <Tag color="purple">Technical</Tag>}
      </Space>

      <Descriptions column={1} size="small" labelStyle={{ width: 130 }} bordered>
        <Descriptions.Item label="Event">
          <code style={{ fontSize: 12 }}>{row.eventType}</code>
        </Descriptions.Item>
        <Descriptions.Item label="Time">{formatLogTime(row.createdAt)}</Descriptions.Item>
        <Descriptions.Item label="Source">{SOURCE_LABEL[row.source] ?? row.source}</Descriptions.Item>
        <Descriptions.Item label="Actor">
          {actorLine(row, refs)}
          {row.actorId && (
            <div>
              <Typography.Text type="secondary" copyable style={{ fontSize: 11 }}>
                {row.actorId}
              </Typography.Text>
            </div>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Target">
          {row.targetType}
          {row.targetId && (
            <div>
              <IdText id={row.targetId} to={targetLink(row.targetType, row.targetId)} />
            </div>
          )}
        </Descriptions.Item>
        {row.userId && (
          <Descriptions.Item label="User">
            {userName && <div>{userName}</div>}
            <IdText id={row.userId} to={adminLinks.user(row.userId)} />
          </Descriptions.Item>
        )}
        {row.listenerId && (
          <Descriptions.Item label="Listener">
            {listenerName && <div>{listenerName}</div>}
            <IdText id={row.listenerId} to={adminLinks.listener(row.listenerId)} />
          </Descriptions.Item>
        )}
        {row.callId && (
          <Descriptions.Item label="Call">
            <IdText id={row.callId} to={adminLinks.call(row.callId)} />
          </Descriptions.Item>
        )}
        {row.transactionId && (
          <Descriptions.Item label="Transaction">
            <IdText id={row.transactionId} />
          </Descriptions.Item>
        )}
        {row.paymentId && (
          <Descriptions.Item label="Payment">
            <IdText id={row.paymentId} to={adminLinks.recharge(row.paymentId)} />
          </Descriptions.Item>
        )}
        <Descriptions.Item label="Request ID">
          {row.requestId ? (
            <Space size={6} wrap>
              <Typography.Text copyable style={{ fontSize: 12 }}>
                {row.requestId}
              </Typography.Text>
              {onFilter && <a onClick={() => onFilter({ request_id: row.requestId! })}>Show this request</a>}
            </Space>
          ) : (
            '—'
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Correlation ID">
          {row.correlationId ? (
            <Space size={6} wrap>
              <Typography.Text copyable style={{ fontSize: 12 }}>
                {row.correlationId}
              </Typography.Text>
              {onFilter && <a onClick={() => onFilter({ correlation_id: row.correlationId! })}>Show whole flow</a>}
            </Space>
          ) : (
            '—'
          )}
        </Descriptions.Item>
        {row.message && <Descriptions.Item label="Message">{row.message}</Descriptions.Item>}
        {row.errorCode && (
          <Descriptions.Item label="Error code">
            <code style={{ fontSize: 12 }}>{row.errorCode}</code>
          </Descriptions.Item>
        )}
      </Descriptions>

      {Object.keys(metaRest).length > 0 && (
        <Card size="small" title="Metadata">
          <Json value={metaRest} />
        </Card>
      )}
      {(row.oldValues || row.newValues) && (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Card size="small" title="Old values">
            {row.oldValues ? <Json value={row.oldValues} /> : '—'}
          </Card>
          <Card size="small" title="New values">
            {row.newValues ? <Json value={row.newValues} /> : '—'}
          </Card>
        </Space>
      )}

      <Card size="small" title="Technical">
        <Descriptions column={1} size="small" labelStyle={{ width: 120 }}>
          <Descriptions.Item label="Platform">{row.platform ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="App version">{row.appVersion ?? '—'}</Descriptions.Item>
          {endpoint && <Descriptions.Item label="Endpoint">{endpoint}</Descriptions.Item>}
          {duration && <Descriptions.Item label="Duration">{duration}</Descriptions.Item>}
          {row.ipAddress && <Descriptions.Item label="IP">{row.ipAddress}</Descriptions.Item>}
          {row.deviceId && <Descriptions.Item label="Device">{row.deviceId}</Descriptions.Item>}
          {row.userAgent && <Descriptions.Item label="User agent">{row.userAgent}</Descriptions.Item>}
          {row.errorMessage && <Descriptions.Item label="Error">{row.errorMessage}</Descriptions.Item>}
        </Descriptions>
        {stack && (
          <div style={{ marginTop: 8 }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Stack trace
            </Typography.Text>
            <Json value={stack} />
          </div>
        )}
      </Card>
    </Space>
  );
}
